import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
  Tabs,
  Tab,
  Avatar,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import ImageIcon from "@mui/icons-material/Image";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadCsv } from "../../utils/exportCsv";
import { projectAPI, ticketAPI } from "../../api";
import { useToast } from "../../components/common/Toast";
import { useTheme, useMediaQuery } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const BASE_URL = API_URL.endsWith("/api") ? API_URL.slice(0, -4) : API_URL;
const UPLOADS_URL = `${BASE_URL}/uploads`;

const STATUS_COLORS = {
  open: { bg: "#C6282818", color: "#C62828" },
  in_progress: { bg: "#E6510018", color: "#E65100" },
  resolved: { bg: "#1565C018", color: "#1565C0" },
  closed: { bg: "#42424218", color: "#424242" },
};
const ROLE_COLORS = {
  admin: "#FF6B35", support_team: "#4472C4", team_member: "#2E7D32",
  qa_team: "#7B1FA2", higher_authority: "#C62828",
};

const ALL_STATUSES = ["open", "in_progress", "resolved", "closed"];

// Matches the backend's parsing (ticketController.js) so the Month filter
// lines up with the same chronological sort the list already uses. Handles
// both "October2026-021" (full month, hyphen) and "OCT2026035" (abbreviated
// month, no hyphen).
const MONTH_INDEX = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8,
  sep: 9, sept: 9, september: 9, oct: 10, october: 10,
  nov: 11, november: 11, dec: 12, december: 12,
};
const MONTH_OPTIONS = [
  [1, "January"], [2, "February"], [3, "March"], [4, "April"],
  [5, "May"], [6, "June"], [7, "July"], [8, "August"],
  [9, "September"], [10, "October"], [11, "November"], [12, "December"],
];
const parseTicketNumber = (tn) => {
  const m = String(tn || "").match(/^([A-Za-z]+)\s*(\d{4})-?(\d+)$/);
  if (!m) return { year: 0, month: 0, seq: 0 };
  return { year: parseInt(m[2], 10), month: MONTH_INDEX[m[1].toLowerCase()] || 0, seq: parseInt(m[3], 10) };
};

// Keeps every Select dropdown anchored directly under its field with a capped
// height, so it doesn't drift or overflow oddly inside a scrollable Dialog.
const SELECT_MENU_PROPS = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  PaperProps: { style: { maxHeight: 260 } },
};

// Default "Ticket Number" prefix: current MonthYear (e.g. "July2026-").
// Admin/support types the trailing number themselves when creating the ticket.
const getDefaultTicketNumber = () => {
  const now = new Date();
  const month = now.toLocaleString("en-US", { month: "long" });
  return `${month}${now.getFullYear()}-`;
};

const emptyForm = {
  ticket_number: "",
  title: "",
  description: "",
  image: null,
  est_end_date: "",
};

// Multipart request helper (mirrors the defects module pattern)
const ticketRequest = async (method, url, data, token) => {
  const formData = new FormData();
  Object.entries(data).forEach(([k, v]) => {
    if (k === "image" && v) formData.append("image", v);
    else if (v !== null && v !== undefined && v !== "" && k !== "image") formData.append(k, v);
  });
  return axios({
    method,
    url: `${API_URL}${url}`,
    data: formData,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "multipart/form-data",
    },
    withCredentials: true,
  });
};

function StatusChip({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.open;
  return (
    <Chip
      size="small"
      label={(status || "").replace("_", " ")}
      sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, textTransform: "capitalize", fontSize: "0.7rem" }}
    />
  );
}

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2.5 }}>{children}</Box> : null;
}

export default function TicketsPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const canCreate = ["support_team", "admin"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const location = useLocation();

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [projectMembers, setProjectMembers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editTicket, setEditTicket] = useState(null);
  const [viewTicket, setViewTicket] = useState(null);
  const [statusDraft, setStatusDraft] = useState("");
  const [filters, setFilters] = useState({ status: "", month: "", year: "" });
  const [form, setForm] = useState(emptyForm);

  // Dialog tab state (Ticket Details / Team Members) — mirrors the Projects edit page
  const [tab, setTab] = useState(0);
  const [ticketMembers, setTicketMembers] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [selectedMemberToAdd, setSelectedMemberToAdd] = useState("");

  // Support/Admin pick a project to work within; other roles just see their own tickets
  useEffect(() => {
    if (!canCreate) return;
    // Backend paginates (default 10/page) — request a high limit so the
    // project picker lists every project, not just the first page.
    projectAPI.getAll({ limit: 1000 }).then((res) => {
      const ps = res.data.projects || [];
      setProjects(ps);
    });
  }, [canCreate]);

  // If we were sent here after creating a project from inside the New
  // Ticket modal's "Add Project" shortcut, reopen that modal with the
  // newly created project pre-selected.
  useEffect(() => {
    if (location.state?.reopenNewTicket) {
      setSelectedProject(location.state.newProjectId || "");
      openCreate();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  useEffect(() => {
    if (canCreate && !selectedProject) return;
    loadTickets();
    if (canCreate && selectedProject && selectedProject !== "all") {
      projectAPI
        .getMembers(selectedProject)
        .then((res) => setProjectMembers(res.data))
        .catch(() => {});
    } else if (selectedProject === "all") {
      setProjectMembers([]);
    }
    // eslint-disable-next-line
  }, [selectedProject, filters, canCreate]);

  const loadTickets = () => {
    setLoading(true);
    const params = {};
    if (filters.status) params.status = filters.status;

    const request = canCreate
      ? selectedProject === "all"
        ? ticketAPI.getAll(params)
        : ticketAPI.getForProject(selectedProject, params)
      : ticketAPI.getAll(params);

    request
      .then((res) => setTickets(res.data))
      .catch(() => showToast("Failed to load tickets", "error"))
      .finally(() => setLoading(false));
  };

  const loadTicketMembers = (projectId, ticketId) => {
    setMemberLoading(true);
    ticketAPI
      .getMembers(projectId, ticketId)
      .then((res) => setTicketMembers(res.data))
      .catch(() => {})
      .finally(() => setMemberLoading(false));
  };

  const handleSubmit = async () => {
    if (!form.ticket_number) return showToast("Ticket number is required", "error");
    if (!editTicket && (!selectedProject || selectedProject === "all")) {
      return showToast("Please select a project", "error");
    }
    if (!form.title) return showToast("Ticket title is required", "error");
    try {
      if (editTicket) {
        const res = await ticketRequest(
          "put",
          `/projects/${selectedProject}/tickets/${editTicket.id}`,
          form,
          token
        );
        setEditTicket(res.data);
        showToast("Ticket updated successfully", "success");
        loadTickets();
      } else {
        const res = await ticketRequest("post", `/projects/${selectedProject}/tickets`, form, token);
        // Mirror the Projects flow: after creation, move straight into managing team members
        setEditTicket(res.data);
        setTab(1);
        setTicketMembers([]);
        showToast("Ticket created! Add team members below.", "success");
        loadTickets();
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save ticket", "error");
    }
  };

  const openCreate = () => {
    setEditTicket(null);
    setForm({ ...emptyForm, ticket_number: getDefaultTicketNumber() });
    setTab(0);
    setTicketMembers([]);
    setOpenForm(true);
  };

  const openEdit = (ticket) => {
    setEditTicket(ticket);
    setForm({
      ticket_number: ticket.ticket_number || "",
      title: ticket.title || "",
      description: ticket.description || "",
      image: null,
      est_end_date: ticket.est_end_date || "",
    });
    setSelectedProject(ticket.project_id);
    setTab(0);
    loadTicketMembers(ticket.project_id, ticket.id);
    setOpenForm(true);
  };

  const handleAddTicketMember = async () => {
    if (!selectedMemberToAdd || !editTicket) return;
    try {
      await ticketAPI.addMember(selectedProject, editTicket.id, { user_id: selectedMemberToAdd });
      setSelectedMemberToAdd("");
      loadTicketMembers(selectedProject, editTicket.id);
      loadTickets();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to add member", "error");
    }
  };

  const handleRemoveTicketMember = async (userId) => {
    if (!editTicket) return;
    if (!window.confirm("Remove this member from the ticket?")) return;
    try {
      await ticketAPI.removeMember(selectedProject, editTicket.id, userId);
      loadTicketMembers(selectedProject, editTicket.id);
      loadTickets();
    } catch {
      showToast("Failed to remove member", "error");
    }
  };

  // Assignees (non support/admin) can only move the status of tickets assigned to them
  const handleStatusUpdate = async (ticket, status) => {
    try {
      await ticketRequest("put", `/projects/${ticket.project_id}/tickets/${ticket.id}`, { status }, token);
      showToast("Status updated", "success");
      setViewTicket(null);
      loadTickets();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to update status", "error");
    }
  };

  // Admin-only — the route itself also enforces this.
  const handleDeleteTicket = async (ticket) => {
    if (!window.confirm(`Delete ticket "${ticket.ticket_number}"? This cannot be undone.`)) return;
    try {
      await ticketAPI.remove(ticket.project_id, ticket.id);
      showToast("Ticket deleted", "success");
      if (viewTicket?.id === ticket.id) setViewTicket(null);
      loadTickets();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete ticket", "error");
    }
  };

  const availableMembers = projectMembers.filter(
    (m) => !ticketMembers.some((tm) => tm.user_id === (m.member?.id || m.user_id))
  );

  const isCurrentUserAssignee = (ticket) =>
    ticket?.assignees?.some((a) => a.user_id === user?.id);

  // Show the Project column whenever tickets from more than one project could
  // be in the list — either a non-creator's cross-project "My Tickets" view,
  // or support/admin browsing "All Projects".
  const showProjectCol = !canCreate || selectedProject === "all";

  // Month filter is applied client-side (the ticket number's month isn't a
  // server-side filter) — `tickets` is already sorted descending by the
  // backend, and filtering preserves that order.
  // Year options: current year back to 2016, newest first.
  const yearOptions = Array.from({ length: new Date().getFullYear() - 2016 + 1 }, (_, i) => new Date().getFullYear() - i);
  const displayedTickets = tickets.filter((t) => {
    const n = parseTicketNumber(t.ticket_number);
    if (filters.month && n.month !== Number(filters.month)) return false;
    if (filters.year && n.year !== Number(filters.year)) return false;
    return true;
  });

  const handleDownload = () => {
    if (!displayedTickets.length) return showToast("No tickets to download", "error");
    downloadCsv(
      `tickets-${new Date().toISOString().slice(0, 10)}.csv`,
      ["#", "Ticket No.", "Title", "Project", "Status", "Assignees", "Est. End Date", "Description"],
      displayedTickets.map((t, i) => [
        i + 1, t.ticket_number, t.title, t.Project?.name || "", (t.status || "").replace(/_/g, " "),
        (t.assignees || []).map((a) => a.member?.name).filter(Boolean).join(", "),
        t.est_end_date || "", t.description || "",
      ])
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: "1.6rem", sm: "2rem" } }}>
            Tickets
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 0.3 }}>
            {canCreate ? "Create and track tickets across projects" : "Tickets assigned to you"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ whiteSpace: "nowrap" }} onClick={handleDownload}>
          Download
        </Button>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            onClick={openCreate}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              New{" "}
            </Box>
            Ticket
          </Button>
        )}
        </Stack>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: "16px !important" }}>
          <Grid container spacing={2} alignItems="center">
            {canCreate && (
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={[{ id: "all", name: "All Projects" }, ...projects]}
                  getOptionLabel={(opt) => opt.name || ""}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  value={
                    selectedProject === "all"
                      ? { id: "all", name: "All Projects" }
                      : projects.find((p) => p.id === selectedProject) || null
                  }
                  onChange={(_, newVal) => setSelectedProject(newVal ? newVal.id : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Project"
                      placeholder="Search projects..."
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                fullWidth
                label="Status"
                value={filters.status}
                size="small"
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="">All Status</MenuItem>
                {ALL_STATUSES.map((s) => (
                  <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>
                    {s.replace("_", " ")}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                fullWidth
                label="Year"
                value={filters.year}
                size="small"
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              >
                <MenuItem value="">All Years</MenuItem>
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                fullWidth
                label="Month"
                value={filters.month}
                size="small"
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setFilters({ ...filters, month: e.target.value })}
              >
                <MenuItem value="">All Months</MenuItem>
                {MONTH_OPTIONS.map(([val, label]) => (
                  <MenuItem key={val} value={val}>{label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                sx={{ height: 40 }}
                onClick={() => setFilters({ status: "", month: "", year: "" })}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow
                  sx={{
                    "& .MuiTableCell-head": {
                      bgcolor: "#F4F7FB",
                      fontWeight: 700,
                      color: "#1E3A5F",
                      fontSize: "0.75rem",
                      py: 1.5,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell width={40}>#</TableCell>
                  <TableCell>Title</TableCell>
                  {showProjectCol && <TableCell>Project</TableCell>}
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>Team Members</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>Est. End Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedTickets.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={showProjectCol ? 7 : 6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary" variant="body2">
                        {canCreate && !selectedProject
                          ? "Search and select a project above to view its tickets"
                          : filters.month || filters.year
                          ? "No tickets found for that month/year"
                          : "No tickets found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {displayedTickets.map((t, idx) => {
                  const names = (t.assignees || []).map((a) => a.member?.name).filter(Boolean);
                  return (
                    <TableRow key={t.id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <ConfirmationNumberIcon sx={{ fontSize: 16, color: "text.disabled", mt: 0.3 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {t.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t.ticket_number}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      {showProjectCol && (
                        <TableCell>
                          <Typography variant="body2">{t.Project?.name || "-"}</Typography>
                        </TableCell>
                      )}
                      <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                        {names.length === 0 ? (
                          <Typography variant="body2" color="text.disabled">
                            Unassigned
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            {names.slice(0, 2).join(", ")}
                            {names.length > 2 ? ` +${names.length - 2}` : ""}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={t.status} />
                      </TableCell>
                      <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                        {t.est_end_date || "—"}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", flexWrap: "nowrap" }}>
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setViewTicket(t);
                                setStatusDraft(t.status);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canCreate && (
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => openEdit(t)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isAdmin && (
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => handleDeleteTicket(t)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {editTicket ? "Edit Ticket" : "New Ticket"}
          <IconButton size="small" onClick={() => setOpenForm(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {editTicket && (
          <Box sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Ticket Details" />
              <Tab label={`Team Members (${ticketMembers.length})`} />
            </Tabs>
          </Box>
        )}

        <DialogContent dividers={!editTicket}>
          {/* ── Ticket Details ── */}
          <TabPanel value={editTicket ? tab : 0} index={0}>
            <Stack spacing={2.5} sx={{ mt: 0.5 }}>
              <TextField
                fullWidth
                label="Ticket Number"
                size="small"
                value={form.ticket_number}
                onChange={(e) => setForm({ ...form, ticket_number: e.target.value })}
                required
                helperText="Format: MonthYear-Number, e.g. July2026-001"
              />

              {!editTicket && (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={projects}
                    getOptionLabel={(opt) => opt.name || ""}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    value={projects.find((p) => p.id === selectedProject) || null}
                    onChange={(_, newVal) => setSelectedProject(newVal ? newVal.id : "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Project"
                        placeholder="Search projects..."
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    )}
                  />
                  <Tooltip title="Create a new project">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={() => navigate("/projects/new", { state: { returnTo: "/tickets" } })}
                      sx={{ whiteSpace: "nowrap", flexShrink: 0, mt: 0.25, height: 40 }}
                    >
                      Add Project
                    </Button>
                  </Tooltip>
                </Stack>
              )}

              <TextField
                fullWidth
                label="Ticket Title"
                size="small"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Ticket Details"
                size="small"
                multiline
                minRows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />

              <Box>
                <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} size="small">
                  {form.image ? form.image.name : "Attach Image"}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => setForm({ ...form, image: e.target.files[0] })}
                  />
                </Button>
                {editTicket?.image && !form.image && (
                  <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
                    Existing image will be kept unless a new one is selected
                  </Typography>
                )}
              </Box>

              <TextField
                fullWidth
                type="date"
                label="Est. End Date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={form.est_end_date}
                onChange={(e) => setForm({ ...form, est_end_date: e.target.value })}
              />

              {editTicket && (
                <TextField
                  select
                  SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                  fullWidth
                  label="Status"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={form.status || editTicket.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {ALL_STATUSES.map((s) => (
                    <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>
                      {s.replace("_", " ")}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Stack>
          </TabPanel>

          {/* ── Team Members (only once the ticket exists) ── */}
          {editTicket && (
            <TabPanel value={tab} index={1}>
              <Stack spacing={2.5}>
                <Typography variant="body2" color="text.secondary">
                  Add project members to this ticket — they'll see it under "My Tickets" and can update its status.
                </Typography>

                <Stack direction="row" spacing={1}>
                  <TextField
                    select
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                    fullWidth
                    size="small"
                    label="Add Member"
                    InputLabelProps={{ shrink: true }}
                    value={selectedMemberToAdd}
                    onChange={(e) => setSelectedMemberToAdd(e.target.value)}
                    helperText={availableMembers.length === 0 ? "No more project members to add" : ""}
                  >
                    {availableMembers.map((m) => (
                      <MenuItem key={m.member?.id || m.user_id} value={m.member?.id || m.user_id}>
                        {m.member?.name || m.member?.email || `User #${m.member?.id || m.user_id}`}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    disabled={!selectedMemberToAdd}
                    onClick={handleAddTicketMember}
                    sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    Add
                  </Button>
                </Stack>

                {memberLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Member</TableCell>
                          <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>System Role</TableCell>
                          <TableCell align="center">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ticketMembers.map((tm) => (
                          <TableRow key={tm.id} hover>
                            <TableCell>
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Avatar
                                  sx={{
                                    width: 30, height: 30,
                                    bgcolor: (ROLE_COLORS[tm.member?.role] || "#888") + "30",
                                    color: ROLE_COLORS[tm.member?.role] || "#888",
                                    fontSize: "0.75rem", fontWeight: 700,
                                  }}
                                >
                                  {tm.member?.name?.charAt(0)}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{tm.member?.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">{tm.member?.email}</Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                              <Chip
                                label={tm.member?.role?.replace(/_/g, " ")}
                                size="small"
                                sx={{
                                  bgcolor: (ROLE_COLORS[tm.member?.role] || "#888") + "18",
                                  color: ROLE_COLORS[tm.member?.role] || "#888",
                                  fontWeight: 700, fontSize: "0.65rem", textTransform: "capitalize",
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Remove from ticket">
                                <IconButton size="small" color="error" onClick={() => handleRemoveTicketMember(tm.user_id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                        {ticketMembers.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                              No members added yet.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </TabPanel>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" onClick={() => setOpenForm(false)}>
            {editTicket ? "Close" : "Cancel"}
          </Button>
          {(!editTicket || tab === 0) && (
            <Button variant="contained" onClick={handleSubmit}>
              {editTicket ? "Save Changes" : "Create Ticket"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      {viewTicket && (
        <Dialog open onClose={() => setViewTicket(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{viewTicket.title}</Typography>
              <Typography variant="caption" color="text.secondary">{viewTicket.ticket_number}</Typography>
            </Box>
            <IconButton size="small" onClick={() => setViewTicket(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2.5}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.65rem" }}>
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <StatusChip status={viewTicket.status} />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.65rem" }}>
                    Est. End Date
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {viewTicket.est_end_date || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.65rem" }}>
                    Team Members
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5, gap: 1 }}>
                    {(viewTicket.assignees || []).length === 0 ? (
                      <Typography variant="body2" color="text.disabled">Unassigned</Typography>
                    ) : (
                      viewTicket.assignees.map((a) => (
                        <Chip key={a.id} size="small" label={a.member?.name || `User #${a.user_id}`} />
                      ))
                    )}
                  </Stack>
                </Grid>
              </Grid>

              {viewTicket.description && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.65rem" }}>
                    Ticket Details
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                    {viewTicket.description}
                  </Typography>
                </Box>
              )}

              <Divider />

              {viewTicket.image ? (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.65rem" }}>
                    Image
                  </Typography>
                  <Box sx={{ mt: 1, border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden", bgcolor: "#f5f5f5", textAlign: "center" }}>
                    <img
                      src={`${UPLOADS_URL}/${viewTicket.image}`}
                      alt="ticket attachment"
                      style={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain", display: "block", margin: "0 auto", cursor: "pointer" }}
                      onClick={() => window.open(`${UPLOADS_URL}/${viewTicket.image}`, "_blank")}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  </Box>
                </Box>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "text.disabled" }}>
                  <ImageIcon fontSize="small" />
                  <Typography variant="body2">No image attached</Typography>
                </Stack>
              )}

              {/* Assignee (non support/admin) can move the ticket through its status */}
              {!canCreate && isCurrentUserAssignee(viewTicket) && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.65rem" }}>
                    Update Status
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <TextField
                      select
                      SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                      size="small"
                      value={statusDraft}
                      onChange={(e) => setStatusDraft(e.target.value)}
                      sx={{ minWidth: 160 }}
                    >
                      {ALL_STATUSES.map((s) => (
                        <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>
                          {s.replace("_", " ")}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleStatusUpdate(viewTicket, statusDraft)}
                      disabled={statusDraft === viewTicket.status}
                    >
                      Update
                    </Button>
                  </Stack>
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            {isAdmin && (
              <Button
                color="error"
                startIcon={<DeleteIcon fontSize="small" />}
                onClick={() => handleDeleteTicket(viewTicket)}
                sx={{ mr: "auto" }}
              >
                Delete
              </Button>
            )}
            <Button variant="outlined" onClick={() => setViewTicket(null)}>
              Close
            </Button>
            {canCreate && (
              <Button
                variant="contained"
                onClick={() => {
                  setViewTicket(null);
                  openEdit(viewTicket);
                }}
              >
                Edit
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}